import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function Register() {
  const nav = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({
    role: "fpo", name: "", email: "", password: "",
    org_name: "", location: "", phone: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const u = await register(form);
      toast.success(`Welcome to Gudlink, ${u.name}`);
      nav(u.role === "buyer" ? "/buyer" : "/fpo");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 grid md:grid-cols-2 gap-12 items-start">
      <div className="hidden md:block">
        <div className="text-xs uppercase tracking-[0.28em] text-secondary">Join the pilot</div>
        <h1 className="font-marketing text-5xl leading-tight mt-3">A signature the market can actually verify.</h1>
        <p className="text-muted-foreground mt-4 max-w-md">
          Register as an FPO/Processor to list batches, or as a Buyer to scan and verify before you commit.
        </p>
        <ul className="mt-6 space-y-2 text-sm text-muted-foreground list-disc pl-5">
          <li>Onboarding takes minutes, not weeks.</li>
          <li>Every batch gets its own tamper-evident record.</li>
          <li>Public verification page — no login needed for scans.</li>
        </ul>
      </div>

      <form onSubmit={submit} className="bg-card border border-border rounded-2xl p-8 shadow-sm" data-testid="register-form">
        <h2 className="font-heading text-2xl font-semibold">Create account</h2>

        <div className="mt-4">
          <Label>I am a</Label>
          <RadioGroup value={form.role} onValueChange={(v) => set("role", v)} className="grid grid-cols-2 gap-3 mt-2" data-testid="register-role">
            <label className={`flex items-center gap-3 rounded-xl border p-4 cursor-pointer ${form.role === "fpo" ? "border-primary bg-primary/5" : "border-border"}`}>
              <RadioGroupItem value="fpo" id="r-fpo" data-testid="role-fpo"/>
              <div>
                <div className="font-medium">FPO / Processor</div>
                <div className="text-xs text-muted-foreground">List and certify batches</div>
              </div>
            </label>
            <label className={`flex items-center gap-3 rounded-xl border p-4 cursor-pointer ${form.role === "buyer" ? "border-primary bg-primary/5" : "border-border"}`}>
              <RadioGroupItem value="buyer" id="r-buyer" data-testid="role-buyer"/>
              <div>
                <div className="font-medium">B2B Buyer</div>
                <div className="text-xs text-muted-foreground">Scan &amp; verify before ordering</div>
              </div>
            </label>
          </RadioGroup>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <div><Label>Full name</Label><Input value={form.name} onChange={e=>set("name", e.target.value)} required data-testid="reg-name"/></div>
          <div><Label>Email</Label><Input type="email" value={form.email} onChange={e=>set("email", e.target.value)} required data-testid="reg-email"/></div>
          <div><Label>Password</Label><Input type="password" value={form.password} onChange={e=>set("password", e.target.value)} required minLength={6} data-testid="reg-password"/></div>
          <div><Label>Phone</Label><Input value={form.phone} onChange={e=>set("phone", e.target.value)} data-testid="reg-phone"/></div>
          <div className="sm:col-span-2"><Label>{form.role === "fpo" ? "FPO / Company name" : "Company / Organization"}</Label><Input value={form.org_name} onChange={e=>set("org_name", e.target.value)} data-testid="reg-org"/></div>
          <div className="sm:col-span-2"><Label>Location</Label><Input placeholder="Kolhapur, Maharashtra" value={form.location} onChange={e=>set("location", e.target.value)} data-testid="reg-location"/></div>
        </div>

        {error && <div className="text-sm text-destructive mt-3" data-testid="register-error">{error}</div>}
        <Button type="submit" disabled={loading} className="w-full mt-6 bg-primary hover:bg-primary/90" data-testid="register-submit">
          {loading ? "Creating account…" : "Create account"}
        </Button>
        <div className="mt-4 text-sm">Already have an account? <Link to="/login" className="text-primary underline underline-offset-4">Sign in</Link></div>
      </form>
    </div>
  );
}
